import { Linking, Alert } from 'react-native';
import APIService from './api';

interface WhatsAppMessage {
  phone: string;
  message: string;
}

interface OrderWhatsAppData {
  orderNumber: string;
  total: number;
  walletPayment?: number;
  newWalletBalance?: number;
  customerName?: string;
  items?: Array<{ name: string; quantity: number; price: number }>;
}

interface ProductInquiryData {
  productName: string;
  productPrice: number;
  productId?: number;
}

interface TwilioResponse {
  success: boolean;
  message?: string;
  fallback?: boolean;
  data?: any;
}

/**
 * Servicio centralizado para manejo de mensajes de WhatsApp
 * Proporciona funciones para enviar diferentes tipos de mensajes
 * 
 * Soporta dos métodos:
 * 1. Twilio API (automático, requiere configuración)
 * 2. WhatsApp URL Scheme (fallback, requiere acción del usuario)
 */
class WhatsAppService {
  
  /**
   * Formatea el número de teléfono para WhatsApp
   * Elimina caracteres no numéricos y agrega código de país si es necesario
   */
  private formatPhoneNumber(phone: string): string {
    // Eliminar todos los caracteres no numéricos
    const cleaned = phone.replace(/\D/g, '');
    
    // Si el número tiene 8 dígitos, asumir que es de Guatemala y agregar código de país
    if (cleaned.length === 8) {
      return `502${cleaned}`;
    }
    
    // Si ya tiene código de país, retornar tal cual
    return cleaned;
  }

  /**
   * Genera mensaje de pedido con formato profesional
   */
  generateOrderMessage(data: OrderWhatsAppData): string {
    const walletPayment = Number(data.walletPayment) || 0;
    const newWalletBalance = Number(data.newWalletBalance) || 0;
    const total = Number(data.total) || 0;
    
    const walletInfo = walletPayment > 0
      ? `\n\n💰 *Pago con Billetera Virtual:* Q${walletPayment.toFixed(2)}\n✨ *Saldo Restante:* Q${newWalletBalance.toFixed(2)}`
      : '';

    const itemsDetail = data.items && data.items.length > 0
      ? `\n\n📦 *Productos:*\n${data.items.map(item => {
          const itemPrice = Number(item.price) || 0;
          const itemQuantity = Number(item.quantity) || 1;
          return `• ${itemQuantity}x ${item.name} - Q${(itemPrice * itemQuantity).toFixed(2)}`;
        }).join('\n')}`
      : '';

    const customerInfo = data.customerName 
      ? `👤 *Cliente:* ${data.customerName}\n` 
      : '';

    return `¡Hola! 👋

He realizado un pedido en *MotoMarketGT*:

📋 *Número de Pedido:* ${data.orderNumber}
${customerInfo}💵 *Total:* Q${total.toFixed(2)}${walletInfo}${itemsDetail}

¿Podrían confirmarme el estado de mi pedido?

¡Gracias! 😊`;
  }

  /**
   * Genera mensaje simple para consultas generales
   */
  generateSimpleMessage(message: string): string {
    return `¡Hola! 👋\n\n${message}\n\n¡Gracias! 😊`;
  }

  /**
   * Genera mensaje para consulta de producto
   */
  generateProductInquiryMessage(data: ProductInquiryData): string {
    const idInfo = data.productId ? `\n🆔 *ID:* ${data.productId}` : '';
    
    return `¡Hola! 👋

Me interesa el siguiente producto:

🏍️ *${data.productName}*
💰 *Precio:* Q${data.productPrice.toFixed(2)}${idInfo}

¿Está disponible?
¿Tienen más información sobre este producto?

¡Gracias! 😊`;
  }

  /**
   * Intenta enviar mensaje via Twilio API
   * @returns Objeto con resultado del envío
   */
  private async tryTwilioSend(phone: string, message: string): Promise<TwilioResponse> {
    try {
      console.log('📞 Intentando envío via Twilio...');
      
      const apiUrl = APIService.getBaseURL() + '/twilio/send';
      console.log('🔗 URL de Twilio:', apiUrl);
      console.log('📱 Número destino:', phone);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          to: phone,
          message: message
        })
      });

      console.log('📡 Status HTTP:', response.status);
      const data: TwilioResponse = await response.json();
      console.log('📦 Respuesta de Twilio:', JSON.stringify(data, null, 2));
      
      if (data.success && !data.fallback) {
        console.log('✅ Mensaje enviado via Twilio exitosamente');
        return data;
      }

      console.log('⚠️ Twilio requiere fallback:', data.message || 'Sin mensaje');
      return { ...data, fallback: true };
      
    } catch (error) {
      console.error('❌ Error conectando con Twilio:', error);
      return {
        success: false,
        fallback: true,
        message: 'Error de conexión con Twilio'
      };
    }
  }

  /**
   * Envía mensaje usando el método directo (URL scheme)
   * Este método abre WhatsApp pero requiere que el usuario presione enviar
   */
  private async sendViaDirect(phone: string, message: string): Promise<boolean> {
    try {
      const cleanPhone = this.formatPhoneNumber(phone);
      const encodedMessage = encodeURIComponent(message);
      
      // URL con formato web (mejor compatibilidad cross-platform)
      const webUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
      
      // URL con esquema de app (fallback)
      const appUrl = `whatsapp://send?phone=${cleanPhone}&text=${encodedMessage}`;
      
      console.log('📱 Enviando via método directo (URL)');
      console.log('📞 Número:', cleanPhone);
      
      // Intentar abrir WhatsApp (priorizar formato web)
      const canOpenWeb = await Linking.canOpenURL(webUrl);
      
      if (canOpenWeb) {
        await Linking.openURL(webUrl);
        console.log('✅ WhatsApp abierto exitosamente (Web)');
        return true;
      }
      
      // Si falla, intentar con el esquema de app
      const canOpenApp = await Linking.canOpenURL(appUrl);
      
      if (canOpenApp) {
        await Linking.openURL(appUrl);
        console.log('✅ WhatsApp abierto exitosamente (App)');
        return true;
      }
      
      throw new Error('No se puede abrir WhatsApp');
      
    } catch (error) {
      console.error('❌ Error abriendo WhatsApp:', error);
      Alert.alert(
        'WhatsApp no disponible',
        'No se pudo abrir WhatsApp. Por favor verifica que esté instalado.',
        [{ text: 'OK' }]
      );
      return false;
    }
  }

  /**
   * Envía mensaje de WhatsApp con manejo inteligente de fallbacks
   * Intenta Twilio primero, luego método directo si falla
   * @returns true si se envió/abrió WhatsApp exitosamente, false en caso contrario
   */
  async sendMessage({ phone, message }: WhatsAppMessage): Promise<boolean> {
    try {
      console.log('📱 WhatsAppService: Iniciando envío de mensaje');
      console.log('📞 Destino:', phone);
      console.log('📝 Longitud del mensaje:', message.length, 'caracteres');
      
      // PASO 1: Intentar con Twilio
      const twilioResult = await this.tryTwilioSend(phone, message);
      
      if (twilioResult.success && !twilioResult.fallback) {
        console.log('✅ Mensaje enviado automáticamente via Twilio');
        return true;
      }
      
      // PASO 2: Si Twilio falla o requiere fallback, usar método directo
      console.log('🔄 Usando método directo (requiere acción del usuario)');
      return await this.sendViaDirect(phone, message);
      
    } catch (error) {
      console.error('❌ WhatsAppService: Error general:', error);
      return false;
    }
  }

  /**
   * [LEGACY] Envía mensaje solo con método directo (sin Twilio)
   * Mantiene compatibilidad con código anterior
   */
  async sendMessageDirect({ phone, message }: WhatsAppMessage): Promise<boolean> {
    console.log('📱 WhatsAppService: Enviando mensaje (solo método directo)');
    return this.sendViaDirect(phone, message);
  }

  /**
   * Envía mensaje de confirmación de pedido
   * @param data Datos del pedido
   * @param whatsappNumber Número de WhatsApp de la empresa
   */
  async sendOrderConfirmation(data: OrderWhatsAppData, whatsappNumber: string): Promise<boolean> {
    console.log('📦 WhatsAppService: Generando mensaje de confirmación de pedido');
    const message = this.generateOrderMessage(data);
    return this.sendMessage({ phone: whatsappNumber, message });
  }

  /**
   * Envía mensaje de soporte/consulta general
   * @param message Mensaje de consulta
   * @param whatsappNumber Número de WhatsApp de la empresa
   */
  async sendSupportMessage(message: string, whatsappNumber: string): Promise<boolean> {
    console.log('💬 WhatsAppService: Generando mensaje de soporte');
    const formattedMessage = this.generateSimpleMessage(message);
    return this.sendMessage({ phone: whatsappNumber, message: formattedMessage });
  }

  /**
   * Envía mensaje de consulta sobre un producto específico
   * @param data Datos del producto
   * @param whatsappNumber Número de WhatsApp de la empresa
   */
  async sendProductInquiry(data: ProductInquiryData, whatsappNumber: string): Promise<boolean> {
    console.log('🏍️ WhatsAppService: Generando mensaje de consulta de producto');
    const message = this.generateProductInquiryMessage(data);
    return this.sendMessage({ phone: whatsappNumber, message });
  }

  /**
   * Envía mensaje personalizado
   * @param customMessage Mensaje personalizado
   * @param whatsappNumber Número de WhatsApp de destino
   */
  async sendCustomMessage(customMessage: string, whatsappNumber: string): Promise<boolean> {
    console.log('✉️ WhatsAppService: Enviando mensaje personalizado');
    return this.sendMessage({ phone: whatsappNumber, message: customMessage });
  }

  /**
   * Valida que un número de teléfono sea válido
   * @param phone Número de teléfono a validar
   */
  isValidPhoneNumber(phone: string): boolean {
    const cleaned = phone.replace(/\D/g, '');
    // Validar que tenga al menos 8 dígitos (número local) o 11-12 (con código de país)
    return cleaned.length >= 8 && cleaned.length <= 15;
  }

  /**
   * Obtiene el número formateado sin enviarlo (útil para mostrar en UI)
   */
  getFormattedPhone(phone: string): string {
    return this.formatPhoneNumber(phone);
  }
}

export default new WhatsAppService();
